<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import MemberHeader from "@/components/member/MemberHeader.vue";
import DeveloperSidebar from "@/components/developer/DeveloperSidebar.vue";

const { t } = useI18n()
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <DeveloperSidebar />

    <main id="main-content">
      <section
        id="breadcrumb-section"
        class="bg-white border-b border-gray-200 py-4"
      >
        <div class="max-w-7xl mx-auto px-5">
          <nav class="flex items-center space-x-2 text-sm">
            <span class="text-primary hover:underline cursor-pointer"
              >{{ $t('developer.upload_reports.breadcrumb.dashboard') }}</span
            >
            <i class="fas fa-chevron-right text-gray-400 text-xs"></i>
            <span class="text-primary hover:underline cursor-pointer"
              >{{ $t('developer.upload_reports.breadcrumb.project_management') }}</span
            >
            <i class="fas fa-chevron-right text-gray-400 text-xs"></i>
            <span class="text-gray-600">{{ $t('developer.upload_reports.breadcrumb.monitoring_reporting') }}</span>
          </nav>
        </div>
      </section>

      <section
        id="upload-header"
        class="bg-gradient-to-r from-primary to-accent text-white py-8"
      >
        <div class="max-w-7xl mx-auto px-5">
          <div class="flex items-center justify-between">
            <div>
              <h1 class="text-3xl font-bold mb-2">
                {{ $t('developer.upload_reports.title') }}
              </h1>
              <p class="text-gray-200">
                {{ $t('developer.upload_reports.subtitle') }}
              </p>
            </div>
            <div class="text-right">
              <div class="text-sm text-gray-300">{{ $t('developer.upload_reports.project_id') }}</div>
              <div class="text-lg font-semibold">
                {{ $t('developer.upload_reports.project_name') }}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        id="upload-progress"
        class="py-6 bg-white border-b border-gray-200"
      >
        <div class="max-w-7xl mx-auto px-5">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-6">
              <div class="flex items-center space-x-2">
                <div
                  class="w-8 h-8 bg-primary rounded-full flex items-center justify-center"
                >
                  <i class="fas fa-check text-white text-sm"></i>
                </div>
                <span class="text-sm font-medium text-gray-700"
                  >{{ $t('developer.upload_reports.progress.project_registered') }}</span
                >
              </div>
              <div class="w-12 h-px bg-gray-300"></div>
              <div class="flex items-center space-x-2">
                <div
                  class="w-8 h-8 bg-primary rounded-full flex items-center justify-center"
                >
                  <i class="fas fa-upload text-white text-sm"></i>
                </div>
                <span class="text-sm font-medium text-primary"
                  >{{ $t('developer.upload_reports.progress.upload_reports') }}</span
                >
              </div>
              <div class="w-12 h-px bg-gray-300"></div>
              <div class="flex items-center space-x-2">
                <div
                  class="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center"
                >
                  <span class="text-gray-500 text-sm">3</span>
                </div>
                <span class="text-sm text-gray-500">{{ $t('developer.upload_reports.progress.verification') }}</span>
              </div>
              <div class="w-12 h-px bg-gray-300"></div>
              <div class="flex items-center space-x-2">
                <div
                  class="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center"
                >
                  <span class="text-gray-500 text-sm">4</span>
                </div>
                <span class="text-sm text-gray-500">{{ $t('developer.upload_reports.progress.credit_issuance') }}</span>
              </div>
            </div>
            <div class="text-sm text-gray-600">
              {{ $t('developer.upload_reports.progress.next_deadline') }}
              <span class="font-semibold text-error">{{ $t('developer.upload_reports.sample_data.deadline_date') }}</span>
            </div>
          </div>
        </div>
      </section>

      <section id="upload-forms" class="py-8">
        <div class="max-w-7xl mx-auto px-5">
          <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div id="main-upload-area" class="lg:col-span-2">
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h2 class="text-xl font-semibold text-accent mb-6">
                  {{ $t('developer.upload_reports.required_documents_title') }}
                </h2>

                <div id="rmr-upload" class="mb-8">
                  <div class="flex items-center justify-between mb-4">
                    <h3 class="text-lg font-medium text-gray-900">
                      {{ $t('developer.upload_reports.rmr.title') }}
                    </h3>
                    <span
                      class="px-2 py-1 bg-error/10 text-error text-xs rounded-full"
                      >{{ $t('developer.upload_reports.status_labels.required') }}</span
                    >
                  </div>
                  <p class="text-sm text-gray-600 mb-4">
                    {{ $t('developer.upload_reports.rmr.description') }}
                  </p>

                  <div
                    class="upload-zone rounded-lg p-8 text-center cursor-pointer"
                  >
                    <i
                      class="fas fa-cloud-upload-alt text-3xl text-gray-400 mb-4"
                    ></i>
                    <p class="text-lg font-medium text-gray-700 mb-2">
                      {{ $t('developer.upload_reports.rmr.drop_text') }}
                    </p>
                    <p class="text-sm text-gray-500 mb-4">
                      {{ $t('developer.upload_reports.required_documents.rmr.browse_text') }}
                    </p>
                    <button
                      class="bg-primary text-white px-4 py-2 rounded-md text-sm font-medium"
                    >
                      {{ $t('developer.upload_reports.required_documents.browse_files') }}
                    </button>
                    <p class="text-xs text-gray-400 mt-2">
                      {{ $t('developer.upload_reports.required_documents.file_support') }}
                    </p>
                  </div>
                </div>

                <div id="sdr-upload" class="mb-8">
                  <div class="flex items-center justify-between mb-4">
                    <h3 class="text-lg font-medium text-gray-900">
                      {{ $t('developer.upload_reports.required_documents.sdr.title') }}
                    </h3>
                    <span
                      class="px-2 py-1 bg-error/10 text-error text-xs rounded-full"
                      >{{ $t('developer.upload_reports.status_labels.required') }}</span
                    >
                  </div>
                  <p class="text-sm text-gray-600 mb-4">
                    {{ $t('developer.upload_reports.required_documents.sdr.description') }}
                  </p>

                  <div
                    class="upload-zone rounded-lg p-8 text-center cursor-pointer"
                  >
                    <i class="fas fa-file-alt text-3xl text-gray-400 mb-4"></i>
                    <p class="text-lg font-medium text-gray-700 mb-2">
                      {{ $t('developer.upload_reports.required_documents.sdr.drop_text') }}
                    </p>
                    <p class="text-sm text-gray-500 mb-4">
                      {{ $t('developer.upload_reports.required_documents.rmr.browse_text') }}
                    </p>
                    <button
                      class="bg-primary text-white px-4 py-2 rounded-md text-sm font-medium"
                    >
                      {{ $t('developer.upload_reports.required_documents.browse_files') }}
                    </button>
                    <p class="text-xs text-gray-400 mt-2">
                      {{ $t('developer.upload_reports.required_documents.file_support') }}
                    </p>
                  </div>
                </div>

                <div id="verification-upload" class="mb-8">
                  <div class="flex items-center justify-between mb-4">
                    <h3 class="text-lg font-medium text-gray-900">
                      {{ $t('developer.upload_reports.required_documents.verification.title') }}
                    </h3>
                    <span
                      class="px-2 py-1 bg-secondary/20 text-secondary text-xs rounded-full"
                      >{{ $t('developer.upload_reports.status_labels.optional') }}</span
                    >
                  </div>
                  <p class="text-sm text-gray-600 mb-4">
                    {{ $t('developer.upload_reports.required_documents.verification.description') }}
                  </p>

                  <div
                    class="upload-zone rounded-lg p-8 text-center cursor-pointer"
                  >
                    <i
                      class="fas fa-certificate text-3xl text-gray-400 mb-4"
                    ></i>
                    <p class="text-lg font-medium text-gray-700 mb-2">
                      {{ $t('developer.upload_reports.required_documents.verification.drop_text') }}
                    </p>
                    <p class="text-sm text-gray-500 mb-4">
                      {{ $t('developer.upload_reports.required_documents.rmr.browse_text') }}
                    </p>
                    <button
                      class="bg-primary text-white px-4 py-2 rounded-md text-sm font-medium"
                    >
                      {{ $t('developer.upload_reports.required_documents.browse_files') }}
                    </button>
                    <p class="text-xs text-gray-400 mt-2">
                      {{ $t('developer.upload_reports.required_documents.file_support') }}
                    </p>
                  </div>
                </div>

                <div id="reversal-upload" class="mb-8">
                  <div class="flex items-center justify-between mb-4">
                    <h3 class="text-lg font-medium text-gray-900">
                      {{ $t('developer.upload_reports.required_documents.reversal.title') }}
                    </h3>
                    <span
                      class="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full"
                      >{{ $t('developer.upload_reports.status_labels.conditional') }}</span
                    >
                  </div>
                  <p class="text-sm text-gray-600 mb-4">
                    {{ $t('developer.upload_reports.required_documents.reversal.description') }}
                  </p>

                  <div
                    class="upload-zone rounded-lg p-8 text-center cursor-pointer opacity-75"
                  >
                    <i
                      class="fas fa-exclamation-triangle text-3xl text-gray-400 mb-4"
                    ></i>
                    <p class="text-lg font-medium text-gray-700 mb-2">
                      {{ $t('developer.upload_reports.required_documents.reversal.drop_text') }}
                    </p>
                    <p class="text-sm text-gray-500 mb-4">{{ $t('developer.upload_reports.required_documents.reversal.only_if_applicable') }}</p>
                    <button
                      class="bg-gray-500 text-white px-4 py-2 rounded-md text-sm font-medium"
                    >
                      {{ $t('developer.upload_reports.required_documents.browse_files') }}
                    </button>
                    <p class="text-xs text-gray-400 mt-2">
                      {{ $t('developer.upload_reports.required_documents.file_support') }}
                    </p>
                  </div>
                </div>

                <div id="submit-section" class="border-t border-gray-200 pt-6">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="confirm-accuracy"
                        class="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <label
                        for="confirm-accuracy"
                        class="text-sm text-gray-700"
                        >{{ $t('developer.upload_reports.confirmation.title') }}</label
                      >
                    </div>
                    <div class="flex space-x-3">
                      <button
                        class="border border-gray-300 text-gray-700 px-6 py-2 rounded-md text-sm font-medium hover:bg-gray-50"
                      >
                        {{ $t('developer.upload_reports.confirmation.save_draft') }}
                      </button>
                      <button
                        class="bg-primary text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-primary/90"
                      >
                        {{ $t('developer.upload_reports.confirmation.submit_reports') }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div id="sidebar-info" class="space-y-6">
              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h3 class="text-lg font-semibold text-accent mb-4">
                  {{ $t('developer.upload_reports.guidelines.title') }}
                </h3>
                <ul class="space-y-3 text-sm text-gray-600">
                  <li class="flex items-start space-x-2">
                    <i class="fas fa-check-circle text-primary mt-0.5"></i>
                    <span
                      >{{ $t('developer.upload_reports.guidelines.english_translations') }}</span
                    >
                  </li>
                  <li class="flex items-start space-x-2">
                    <i class="fas fa-check-circle text-primary mt-0.5"></i>
                    <span
                      >{{ $t('developer.upload_reports.guidelines.naming_convention') }}</span
                    >
                  </li>
                  <li class="flex items-start space-x-2">
                    <i class="fas fa-check-circle text-primary mt-0.5"></i>
                    <span>{{ $t('developer.upload_reports.guidelines.max_file_size') }}</span>
                  </li>
                  <li class="flex items-start space-x-2">
                    <i class="fas fa-check-circle text-primary mt-0.5"></i>
                    <span
                      >{{ $t('developer.upload_reports.guidelines.digital_signatures') }}</span
                    >
                  </li>
                </ul>
              </div>

              <div class="bg-white rounded-lg border border-gray-200 p-6">
                <h3 class="text-lg font-semibold text-accent mb-4">
                  {{ $t('developer.upload_reports.submission_status.title') }}
                </h3>
                <div class="space-y-3">
                  <div class="flex items-center justify-between">
                    <span class="text-sm text-gray-600">{{ $t('developer.upload_reports.sample_data.rmr_q3_2024') }}</span>
                    <span
                      class="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full"
                      >{{ $t('developer.upload_reports.status_labels.submitted') }}</span
                    >
                  </div>
                  <div class="flex items-center justify-between">
                    <span class="text-sm text-gray-600">{{ $t('developer.upload_reports.sample_data.sdr_2024') }}</span>
                    <span
                      class="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full"
                      >{{ $t('developer.upload_reports.status_labels.approved') }}</span
                    >
                  </div>
                  <div class="flex items-center justify-between">
                    <span class="text-sm text-gray-600">{{ $t('developer.upload_reports.sample_data.rmr_q4_2024') }}</span>
                    <span
                      class="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full"
                      >{{ $t('developer.upload_reports.status_labels.pending') }}</span
                    >
                  </div>
                </div>
              </div>

              <div class="bg-blue-50 rounded-lg border border-blue-200 p-6">
                <h3 class="text-lg font-semibold text-blue-900 mb-2">
                  {{ $t('developer.upload_reports.help.title') }}
                </h3>
                <p class="text-sm text-blue-800 mb-4">
                  {{ $t('developer.upload_reports.help.subtitle') }}
                </p>
                <div class="space-y-2">
                  <button
                    class="w-full text-left text-blue-700 hover:text-blue-900 text-sm flex items-center space-x-2"
                  >
                    <i class="fas fa-download"></i>
                    <span>{{ $t('developer.upload_reports.help.rmr_template') }}</span>
                  </button>
                  <button
                    class="w-full text-left text-blue-700 hover:text-blue-900 text-sm flex items-center space-x-2"
                  >
                    <i class="fas fa-download"></i>
                    <span>{{ $t('developer.upload_reports.help.sdr_guidelines') }}</span>
                  </button>
                  <button
                    class="w-full text-left text-blue-700 hover:text-blue-900 text-sm flex items-center space-x-2"
                  >
                    <i class="fas fa-question-circle"></i>
                    <span>{{ $t('developer.upload_reports.help.contact_support') }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>
